import React, { useState, useEffect } from 'react';
import { Music, Trash2, Plus, Shuffle, List } from 'lucide-react';
import {
  fetchAdminPlaylists,
  createAdminPlaylist,
  updateAdminPlaylist,
  updateAdminPlaylistImage,
  deleteAdminPlaylist,
  generateRandomPlaylist,
  fetchCategories,
  fetchPlaylist,
  addTrackToPlaylist,
  removeTrackFromPlaylist,
  fetchTracks
} from '../services/api';

export default function AdminPlaylistManager() {
  const [playlists, setPlaylists] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showSelectTracksModal, setShowSelectTracksModal] = useState(false);
  const [showDetailView, setShowDetailView] = useState(false);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [newPlaylistName, setNewPlaylistName] = useState('');
  const [newPlaylistDescription, setNewPlaylistDescription] = useState('');
  const [newPlaylistImage, setNewPlaylistImage] = useState(null);
  const [newPlaylistImagePreview, setNewPlaylistImagePreview] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [playlistsData, categoriesData] = await Promise.all([
        fetchAdminPlaylists(),
        fetchCategories()
      ]);
      setPlaylists(playlistsData);
      setCategories(categoriesData);
    } catch (error) {
      console.error('Erreur chargement données:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePlaylist = async () => {
    if (!newPlaylistName.trim()) return;
    
    try {
      console.log('📝 Création playlist:', { 
        name: newPlaylistName, 
        description: newPlaylistDescription,
        hasImage: !!newPlaylistImage 
      });
      
      const formData = new FormData();
      formData.append('name', newPlaylistName);
      formData.append('description', newPlaylistDescription);
      if (newPlaylistImage) {
        formData.append('cover_image', newPlaylistImage);
      }
      
      console.log('📤 Envoi FormData...');
      const result = await createAdminPlaylist(formData);
      console.log('✅ Playlist créée:', result);
      
      setNewPlaylistName('');
      setNewPlaylistDescription('');
      setNewPlaylistImage(null);
      setNewPlaylistImagePreview(null);
      setShowCreateModal(false);
      loadData();
    } catch (error) {
      console.error('❌ Erreur création playlist:', error);
      console.error('Détails:', error.response?.data);
      alert('Erreur lors de la création: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewPlaylistImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewPlaylistImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDeletePlaylist = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette playlist ?')) return;
    
    try {
      await deleteAdminPlaylist(id);
      loadData();
    } catch (error) {
      console.error('Erreur suppression playlist:', error);
    }
  };

  const handleGenerateRandom = async (categoryId, count) => {
    if (!selectedPlaylist || !categoryId) return;
    
    try {
      await generateRandomPlaylist(selectedPlaylist.id, categoryId, count);
      setShowGenerateModal(false);
      setSelectedPlaylist(null);
      loadData();
      alert('Playlist générée avec succès !');
    } catch (error) {
      console.error('Erreur génération playlist:', error);
      alert('Erreur lors de la génération de la playlist');
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Playlists Admin</h2>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
        >
          <Plus size={20} />
          Créer une playlist
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {playlists.map((playlist) => (
            <div
              key={playlist.id}
              className="bg-white/5 rounded-lg p-4 hover:bg-white/10 transition-colors"
            >
              {/* Image preview */}
              <div className="w-full aspect-square rounded-lg mb-3 overflow-hidden">
                {playlist.cover_image ? (
                  <img 
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/playlists/cover/${playlist.cover_image.split('/').pop()}`}
                    alt={playlist.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center text-4xl">
                    {playlist.cover_emoji || '🎵'}
                  </div>
                )}
              </div>

              <div className="flex items-start justify-between mb-3">
                <button
                  onClick={() => {
                    setSelectedPlaylist(playlist);
                    setShowDetailView(true);
                  }}
                  className="flex-1 text-left hover:opacity-80 transition-opacity"
                >
                  <h3 className="font-bold text-lg mb-1">{playlist.name}</h3>
                  <p className="text-sm text-white/60">{playlist.description}</p>
                  <p className="text-xs text-white/40 mt-2">
                    {playlist.track_count || 0} piste(s)
                  </p>
                </button>
                <button
                  onClick={() => handleDeletePlaylist(playlist.id)}
                  className="text-red-500 hover:text-red-400 p-2"
                >
                  <Trash2 size={18} />
                </button>
              </div>
              
              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => {
                    setSelectedPlaylist(playlist);
                    setShowGenerateModal(true);
                  }}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-purple-600 hover:bg-purple-700 rounded text-sm transition-colors"
                >
                  <Shuffle size={16} />
                  Aléatoire
                </button>
                <button
                  onClick={() => {
                    setSelectedPlaylist(playlist);
                    setShowSelectTracksModal(true);
                  }}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded text-sm transition-colors"
                >
                  <List size={16} />
                  Sélectionner
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal Créer une playlist */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 modal-text">
          <div className="bg-zinc-900 rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4">Créer une playlist admin</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm mb-2">Image de couverture</label>
                <div className="relative">
                  {newPlaylistImagePreview ? (
                    <div className="relative w-full aspect-square rounded-lg overflow-hidden mb-2">
                      <img 
                        src={newPlaylistImagePreview} 
                        alt="Aperçu" 
                        className="w-full h-full object-cover"
                      />
                      <button
                        onClick={() => {
                          setNewPlaylistImage(null);
                          setNewPlaylistImagePreview(null);
                        }}
                        className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white rounded-full p-2"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ) : (
                    <div className="w-full aspect-square rounded-lg bg-white/5 border-2 border-dashed border-white/20 flex flex-col items-center justify-center mb-2">
                      <Plus size={32} className="text-white/40 mb-2" />
                      <span className="text-sm text-white/40">Cliquez pour ajouter</span>
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm mb-2">Nom *</label>
                <input
                  type="text"
                  value={newPlaylistName}
                  onChange={(e) => setNewPlaylistName(e.target.value)}
                  className="w-full px-4 py-2 bg-white/10 rounded border border-white/20 focus:border-green-500 outline-none"
                  placeholder="Ex: Top Électro 2024"
                />
              </div>
              
              <div>
                <label className="block text-sm mb-2">Description</label>
                <textarea
                  value={newPlaylistDescription}
                  onChange={(e) => setNewPlaylistDescription(e.target.value)}
                  className="w-full px-4 py-2 bg-white/10 rounded border border-white/20 focus:border-green-500 outline-none resize-none"
                  rows="3"
                  placeholder="Description de la playlist..."
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewPlaylistName('');
                  setNewPlaylistDescription('');
                  setNewPlaylistImage(null);
                  setNewPlaylistImagePreview(null);
                }}
                className="flex-1 px-4 py-2 bg-white/10 hover:bg-white/20 rounded transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleCreatePlaylist}
                disabled={!newPlaylistName.trim()}
                className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Créer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Générer Aléatoire */}
      {showGenerateModal && selectedPlaylist && (
        <GenerateRandomModal
          playlist={selectedPlaylist}
          categories={categories}
          onGenerate={handleGenerateRandom}
          onClose={() => {
            setShowGenerateModal(false);
            setSelectedPlaylist(null);
          }}
        />
      )}

      {/* Modal Sélectionner des pistes */}
      {showSelectTracksModal && selectedPlaylist && (
        <SelectTracksModal
          playlist={selectedPlaylist}
          categories={categories}
          onClose={() => {
            setShowSelectTracksModal(false);
            setSelectedPlaylist(null);
            loadData();
          }}
        />
      )}

      {/* Vue détail de la playlist */}
      {showDetailView && selectedPlaylist && (
        <PlaylistDetailView
          playlist={selectedPlaylist}
          categories={categories}
          onClose={() => {
            setShowDetailView(false);
            setSelectedPlaylist(null);
            loadData();
          }}
        />
      )}
    </div>
  );
}

function GenerateRandomModal({ playlist, categories, onGenerate, onClose }) {
  const [selectedCategory, setSelectedCategory] = useState('');
  const [count, setCount] = useState('');

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 modal-text">
      <div className="bg-zinc-900 rounded-lg p-6 w-full max-w-md">
        <h3 className="text-xl font-bold mb-4">
          Générer aléatoirement - {playlist.name}
        </h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm mb-2">Catégorie *</label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-4 py-2 bg-white/10 rounded border border-white/20 focus:border-purple-500 outline-none"
            >
              <option value="">Choisir une catégorie</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm mb-2">Nombre de pistes (facultatif)</label>
            <input
              type="number"
              value={count}
              onChange={(e) => setCount(e.target.value)}
              min="1"
              max="50"
              placeholder="Toutes les pistes de la catégorie"
              className="w-full px-4 py-2 bg-white/10 rounded border border-white/20 focus:border-purple-500 outline-none"
            />
            <p className="text-xs text-white/40 mt-1">Laissez vide pour toutes les pistes</p>
          </div>
        </div>
        
        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-white/10 hover:bg-white/20 rounded transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={() => onGenerate(selectedCategory, count ? parseInt(count) : null)}
            disabled={!selectedCategory}
            className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Générer
          </button>
        </div>
      </div>
    </div>
  );
}

function SelectTracksModal({ playlist, categories, onClose }) {
  const [tracks, setTracks] = useState([]);
  const [filteredTracks, setFilteredTracks] = useState([]);
  const [selectedTracks, setSelectedTracks] = useState([]);
  const [filterCategory, setFilterCategory] = useState('');
  const [filterSearch, setFilterSearch] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadTracks();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [tracks, filterCategory, filterSearch]);

  const loadTracks = async () => {
    setLoading(true);
    try {
      const tracksData = await fetchTracks();
      setTracks(tracksData);
    } catch (error) {
      console.error('Erreur chargement pistes:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...tracks];

    if (filterCategory) {
      filtered = filtered.filter((t) => t.category_id == filterCategory);
    }

    if (filterSearch) {
      const search = filterSearch.toLowerCase();
      filtered = filtered.filter(
        (t) =>
          t.title.toLowerCase().includes(search) ||
          (t.artist && t.artist.toLowerCase().includes(search))
      );
    }

    setFilteredTracks(filtered);
  };

  const toggleTrack = (trackId) => {
    setSelectedTracks((prev) =>
      prev.includes(trackId)
        ? prev.filter((id) => id !== trackId)
        : [...prev, trackId]
    );
  };

  const handleAddTracks = async () => {
    if (selectedTracks.length === 0) return;

    try {
      for (const trackId of selectedTracks) {
        await addTrackToPlaylist(playlist.id, trackId);
      }
      alert(`${selectedTracks.length} piste(s) ajoutée(s) avec succès !`);
      onClose();
    } catch (error) {
      console.error('Erreur ajout pistes:', error);
      alert('Erreur lors de l\'ajout des pistes');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 modal-text">
      <div className="bg-zinc-900 rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="p-6 border-b border-white/10">
          <h3 className="text-xl font-bold mb-4">
            Ajouter des pistes - {playlist.name}
          </h3>
          
          <div className="flex gap-4">
            <div className="flex-1">
              <input
                type="text"
                value={filterSearch}
                onChange={(e) => setFilterSearch(e.target.value)}
                placeholder="Rechercher par titre ou artiste..."
                className="w-full px-4 py-2 bg-white/10 rounded border border-white/20 focus:border-blue-500 outline-none"
              />
            </div>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-4 py-2 bg-white/10 rounded border border-white/20 focus:border-blue-500 outline-none"
            >
              <option value="">Toutes catégories</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto"></div>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredTracks.map((track) => (
                <label
                  key={track.id}
                  className="flex items-center gap-3 p-3 hover:bg-white/5 rounded cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedTracks.includes(track.id)}
                    onChange={() => toggleTrack(track.id)}
                    className="w-5 h-5"
                  />
                  <div className="flex-1">
                    <div className="font-medium">{track.title}</div>
                    <div className="text-sm text-white/60">
                      {track.artist} • {track.category_name}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="p-6 border-t border-white/10 flex items-center justify-between">
          <span className="text-sm text-white/60">
            {selectedTracks.length} piste(s) sélectionnée(s)
          </span>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={handleAddTracks}
              disabled={selectedTracks.length === 0}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Valider
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PlaylistDetailView({ playlist, categories, onClose }) {
  const [playlistData, setPlaylistData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [playlistName, setPlaylistName] = useState(playlist.name);
  const [playlistDescription, setPlaylistDescription] = useState(playlist.description);
  const [showAddTracksModal, setShowAddTracksModal] = useState(false);

  useEffect(() => {
    loadPlaylistDetails();
  }, [playlist.id]);

  const loadPlaylistDetails = async () => {
    setLoading(true);
    try {
      const data = await fetchPlaylist(playlist.id);
      setPlaylistData(data);
      setPlaylistName(data.name);
      setPlaylistDescription(data.description);
    } catch (error) {
      console.error('Erreur chargement détails:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveTrack = async (trackId) => {
    if (!window.confirm('Retirer cette piste de la playlist ?')) return;
    
    try {
      await removeTrackFromPlaylist(playlist.id, trackId);
      loadPlaylistDetails();
    } catch (error) {
      console.error('Erreur suppression piste:', error);
      alert('Erreur lors de la suppression');
    }
  };

  return (
    <div className="fixed inset-0 bg-black z-50 overflow-y-auto modal-text">
      <div className="min-h-screen">
        {/* Header */}
        <div className="sticky top-0 bg-black/95 backdrop-blur border-b border-white/10 z-10">
          <div className="p-6 flex items-center justify-between">
            <button
              onClick={onClose}
              className="text-white/60 hover:text-white"
            >
              ← Retour
            </button>
            <div className="flex gap-3">
              {editMode ? (
                <>
                  <button
                    onClick={() => setEditMode(false)}
                    className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={() => {
                      // TODO: Implémenter la sauvegarde
                      setEditMode(false);
                    }}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded"
                  >
                    Sauvegarder
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setEditMode(true)}
                  className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded"
                >
                  Modifier
                </button>
              )}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-96">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
          </div>
        ) : (
          <div className="p-6 max-w-6xl mx-auto">
            {/* Playlist Info */}
            <div className="mb-8">
              {editMode ? (
                <div className="space-y-4">
                  <input
                    type="text"
                    value={playlistName}
                    onChange={(e) => setPlaylistName(e.target.value)}
                    className="text-4xl font-bold bg-white/10 px-4 py-2 rounded w-full"
                  />
                  <textarea
                    value={playlistDescription}
                    onChange={(e) => setPlaylistDescription(e.target.value)}
                    className="text-lg bg-white/10 px-4 py-2 rounded w-full resize-none"
                    rows="2"
                  />
                </div>
              ) : (
                <>
                  <h1 className="text-4xl font-bold mb-2">{playlistData?.name}</h1>
                  <p className="text-lg text-white/60 mb-4">{playlistData?.description}</p>
                  <p className="text-sm text-white/40">
                    {playlistData?.tracks?.length || 0} piste(s)
                  </p>
                </>
              )}
            </div>

            {/* Actions */}
            <div className="mb-6">
              <button
                onClick={() => setShowAddTracksModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 rounded"
              >
                <Plus size={18} />
                Ajouter des pistes
              </button>
            </div>

            {/* Tracks List */}
            <div className="bg-white/5 rounded-lg overflow-hidden">
              <div className="grid grid-cols-[auto,1fr,auto,auto] gap-4 px-4 py-3 border-b border-white/10 text-sm text-white/60">
                <span>#</span>
                <span>Titre</span>
                <span>Artiste</span>
                <span className="text-center">Actions</span>
              </div>
              
              {playlistData?.tracks && playlistData.tracks.length > 0 ? (
                <div>
                  {playlistData.tracks.map((track, index) => (
                    <div
                      key={track.id}
                      className="grid grid-cols-[auto,1fr,auto,auto] gap-4 px-4 py-3 border-b border-white/5 hover:bg-white/5 items-center"
                    >
                      <span className="text-white/40">{index + 1}</span>
                      <div>
                        <div className="font-medium">{track.title}</div>
                        <div className="text-sm text-white/60">{track.category_name}</div>
                      </div>
                      <span className="text-white/60">{track.artist || '—'}</span>
                      <button
                        onClick={() => handleRemoveTrack(track.id)}
                        className="text-red-500 hover:text-red-400 p-2"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-12 text-center text-white/40">
                  Aucune piste dans cette playlist
                </div>
              )}
            </div>
          </div>
        )}

        {/* Modal Add Tracks */}
        {showAddTracksModal && (
          <SelectTracksModal
            playlist={playlist}
            categories={categories}
            onClose={() => {
              setShowAddTracksModal(false);
              loadPlaylistDetails();
            }}
          />
        )}
      </div>
    </div>
  );
}
